/**
 * 会话附件的客户端状态（Lulu 闭环目标 3）。
 *
 * # 为什么上传态是客户端的事实
 *
 * 字节进度只有**发起上传的那一端**知道（XHR 的 upload.onprogress）。
 * 服务端那边只有两种状态：这一行在，或者不在——行在就是传完了，
 * 不存在「半存在的附件」。
 *
 * 所以这一份要回答的是服务端答不了的那几个问题：
 *
 *   - 这个文件现在传到哪了
 *   - 传失败的那个，重试按钮该不该亮
 *   - 现在能不能按发送
 *   - 关了页面再回来，服务端那边还留着哪些（那些是 done，不是 uploading）
 */

/** 单个附件在客户端的样子。 */
export interface DraftAttachment {
  /** 本地临时 id。**传完之后仍然用它做 key**——换成服务端 id 会让 React
   *  把这一行整个重建，而重建的那一刻进度条会闪一下回到 0。 */
  readonly localId: string
  readonly name: string
  readonly bytes: number
  readonly mime: string
  readonly state: 'uploading' | 'done' | 'failed'
  /** 0–100。`uploading` 之外的档没有意义，画进度条只看 uploading。 */
  readonly percent: number
  /** 传完之后服务端给的 id。发消息时带的就是它。 */
  readonly serverId?: string
  /** 失败的原因（服务端那句人话）。**没有就不显示一句编的**。 */
  readonly error?: string
}

/**
 * 单个文件收不收（客户端这一半）。
 *
 * **与服务端那份是两份，必须同时改。** 客户端这份的作用是在传之前就说
 * 「这个不行」——省掉一次注定失败的 10MB 上传，以及那几秒的等待。
 * 它**不是**安全边界：真正拒绝的是服务端那三道（元数据、内容嗅探、库 CHECK）。
 *
 * 判据故意只有大小与类型两条：文件名的规矩（非空、≤255）由浏览器保证，
 * 而内容嗅探这一侧做不了——那需要读完整个文件。
 */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024

export const ATTACHMENT_MIME_WHITELIST = Object.freeze([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'application/pdf',
  'text/plain',
  'text/markdown',
  'text/csv',
  'application/json',
  'application/zip',
] as const)

/** `<input accept>` 用的那一串。由白名单派生，**不另写一份**。 */
export const ATTACHMENT_ACCEPT = ATTACHMENT_MIME_WHITELIST.join(',')

export type RejectReason = 'too_large' | 'bad_type'

/** 传之前先看一眼。收就回 null。 */
export function rejectReason(file: {
  readonly size: number
  readonly type: string
}): RejectReason | null {
  if (file.size > MAX_ATTACHMENT_BYTES) return 'too_large'
  if (!(ATTACHMENT_MIME_WHITELIST as readonly string[]).includes(file.type)) return 'bad_type'
  return null
}

/** 一条消息最多挂几个。与服务端 MaxAttachmentsPerMessage 同一个数。 */
export const MAX_ATTACHMENTS_PER_MESSAGE = 10

/**
 * 现在能不能按发送。
 *
 * **有任何一个还在传就不能。** 发出去的话，那几个还没传完的会挂不上，
 * 而服务端的规矩是「一个挂不上就整笔拒绝」——用户会看到一次莫名其妙的
 * 失败，而他刚才明明看到进度条在走。
 *
 * **失败的那些不挡**：它们不会被带走（没有 serverId），
 * 用户可以选择重试或者直接发。挡住的话，一个传不上去的文件会让他
 * 连话都发不出去。
 */
export function canSend(
  draft: string,
  attachments: readonly DraftAttachment[],
): { readonly ok: boolean; readonly why: 'empty' | 'uploading' | null } {
  if (attachments.some((a) => a.state === 'uploading')) return { ok: false, why: 'uploading' }
  const hasText = draft.trim() !== ''
  const hasAttachment = attachments.some((a) => a.state === 'done')
  // 光有附件、一个字没写也能发：设计稿的输入框上附件与文字是并列的两件东西。
  if (!hasText && !hasAttachment) return { ok: false, why: 'empty' }
  return { ok: true, why: null }
}

/**
 * 这一次发送要带走哪些。
 *
 * **只带 done 的。** uploading 的还没有 serverId，failed 的服务端根本没有——
 * 带上任一种都会让整条消息被拒（服务端「一个挂不上就整笔回滚」）。
 */
export function sendableIDs(attachments: readonly DraftAttachment[]): readonly string[] {
  const out: string[] = []
  for (const a of attachments) {
    if (a.state === 'done' && a.serverId) out.push(a.serverId)
  }
  return out
}

/**
 * 还能不能再加。
 *
 * 数的是**全部**（含 failed），不是只数 done：一个失败的还占着一行、
 * 用户还能重试它，把它排除在外会让「已经有 10 个」的那一刻还能再选一个，
 * 然后在发送时被服务端拒。
 */
export function canAddMore(attachments: readonly DraftAttachment[]): boolean {
  return attachments.length < MAX_ATTACHMENTS_PER_MESSAGE
}

/**
 * 服务端那份「传上来了还没发出去」翻成客户端的行。
 *
 * **它们全是 done。** 服务端有这一行就等于传完了——把它们标成 uploading
 * 会让重进页面时凭空出现三条永远走不完的进度条。
 */
export function adoptPending(
  pending: readonly {
    readonly id: string
    readonly name: string
    readonly bytes: number
    readonly mime: string
  }[],
): readonly DraftAttachment[] {
  return pending.map((p) => ({
    localId: 'server:' + p.id,
    name: p.name,
    bytes: p.bytes,
    mime: p.mime,
    state: 'done' as const,
    percent: 100,
    serverId: p.id,
  }))
}

/** 人话的大小。给用户看的，不是给日志看的。 */
export function humanBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  // 一位小数：3.4 MB 比 3 MB 有用，比 3.42 MB 干净。
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
